/**
 * InsightsTab — bucket coverage ring per bucket for the current month
 * (posts vs monthly target), plus a Claude-drafted analysis of the last
 * 90d of @flaxitup posts showing which bucket dominates the actual feed
 * and where to lean in next.
 */
import { useState } from 'react';
import { BarChart3, Eye, Film, Heart, Lightbulb, Loader2, RefreshCw, Sparkles, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { cn } from '@/lib/utils';
import { useBucketCoverage, type BucketCoverage } from '@/hooks/useBucketCoverage';
import { useAccountInsights, syncAccountInsights, type ReelRow, type TopPostRow, type AccountDaily } from '@/hooks/useAccountInsights';
import { useBestTime, type BestTimeCell } from '@/hooks/useBestTime';
import { useBucketPerformance, type BucketPerformance } from '@/hooks/useBucketPerformance';
import { BucketDrilldown } from './BucketDrilldown';

interface BucketAnalysisRow {
  count: number;
  likes: number;
  comments: number;
  views: number;
  sample_captions: string[];
}
interface AnalyzeResp {
  ok: boolean;
  days: number;
  posts_analyzed?: number;
  posts?: number;
  note?: string;
  per_bucket?: Record<string, BucketAnalysisRow>;
  suggested_buckets?: Array<{ slug: string; name: string; description: string; rationale: string }>;
  recommendation?: string;
}

export function InsightsTab() {
  const coverage = useBucketCoverage();
  const account = useAccountInsights(30);
  const bestTime = useBestTime();
  const bucketPerf = useBucketPerformance();
  const [analysis, setAnalysis] = useState<AnalyzeResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncingAccount, setSyncingAccount] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [drilldownSlug, setDrilldownSlug] = useState<string | null>(null);

  async function refreshAccount() {
    setSyncingAccount(true);
    const r = await syncAccountInsights();
    setSyncingAccount(false);
    if (!r.ok) setErr(r.error ?? 'account sync failed');
    else account.refetch();
  }

  async function analyze() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/content/analyze-buckets', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ days: 90 }),
      });
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.error ?? `HTTP ${r.status}`);
      setAnalysis(j);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <BucketDrilldown slug={drilldownSlug} onClose={() => setDrilldownSlug(null)} />
      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><Users className="h-4 w-4 text-violet-500" /> Account health · @flaxitup</span>}
          description="Followers, reach, and profile views for the last 30 days. Sourced directly from Meta Graph API."
          actions={
            <Button size="sm" variant="outline" onClick={refreshAccount} disabled={syncingAccount}>
              {syncingAccount ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Syncing…</> : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</>}
            </Button>
          }
        />
        <AccountCards data={account.data} loading={account.isLoading} />
      </div>

      {(account.data?.daily?.length ?? 0) >= 2 && (
        <div>
          <SectionHeader
            title={<span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-violet-500" /> Follower trend · 30 days</span>}
            description={`Engagement rate: ${account.data?.engagementRate != null ? (account.data.engagementRate * 100).toFixed(2) + '%' : '—'} (top-6 posts, interactions / reach)`}
          />
          <FollowerSparkline daily={account.data!.daily} />
        </div>
      )}

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><Film className="h-4 w-4 text-violet-500" /> Top reels · last 30 days</span>}
          description="Reels ordered by plays. Watch-time in seconds where Meta provides it — lower avg watch = weak hook, iterate."
        />
        <ReelsLeaderboard reels={account.data?.reels ?? []} loading={account.isLoading} />
      </div>

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><Heart className="h-4 w-4 text-violet-500" /> Top posts · last 30 days</span>}
          description="Best-performing posts by total interactions (likes + comments + saves + shares)."
        />
        <TopPostsGrid posts={account.data?.topPosts ?? []} loading={account.isLoading} />
      </div>

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-violet-500" /> Best time to post</span>}
          description="Engagement % by weekday (IST rows) × hour (columns) across the last 180 days. Darker = better historical performance."
        />
        <BestTimeHeatmap cells={bestTime.data ?? []} loading={bestTime.isLoading} />
      </div>

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-violet-500" /> Bucket performance</span>}
          description="Organic + paid ROI per bucket for Studio-authored posts. Numbers fill in as drafts get published + boosted."
        />
        <BucketPerformanceTable rows={bucketPerf.data ?? []} loading={bucketPerf.isLoading} onOpen={setDrilldownSlug} />
      </div>

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-muted-foreground" /> Bucket coverage · this month</span>}
          description="How many posts (drafts, scheduled, and published) each bucket has this month vs its target."
        />
        {coverage.isLoading ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : coverage.isError ? (
          <EmptyState title="Couldn't load coverage" description={String(coverage.error)} />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {coverage.rows.map((r) => <CoverageCard key={r.slug} row={r} onOpen={() => setDrilldownSlug(r.slug)} />)}
          </div>
        )}
      </div>

      <div>
        <SectionHeader
          title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" /> Feed analysis · last 90 days</span>}
          description="Claude reads @flaxitup's most recent posts, classifies each into a bucket, and reports which shape of content is actually running the feed."
          actions={
            <Button size="sm" onClick={analyze} disabled={busy}>
              {busy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Analyzing…</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Run analysis</>}
            </Button>
          }
        />
        {err && <div className="mb-2 rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-sm text-rose-500">{err}</div>}
        {!analysis ? (
          <EmptyState title="No analysis yet" description="Run the analysis to see where the feed is landing today." />
        ) : (
          <AnalysisResult r={analysis} coverage={coverage.rows} />
        )}
      </div>
    </div>
  );
}

function AccountCards({ data, loading }: { data: ReturnType<typeof useAccountInsights>['data']; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
      </div>
    );
  }
  if (!data?.latest) {
    return (
      <EmptyState
        title="No account snapshot yet"
        description="Hit Refresh above to pull today's followers / reach / profile views from Meta."
      />
    );
  }
  const { latest, followerDelta } = data;
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
      <StatCard icon={<Users className="h-4 w-4" />} label="Followers" value={latest.followers_count} delta={followerDelta} deltaLabel="30d" />
      <StatCard icon={<Eye className="h-4 w-4" />} label="Reach · today" value={latest.reach} tone="emerald" />
      <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Profile views · today" value={latest.profile_views} tone="violet" />
      <StatCard icon={<Heart className="h-4 w-4" />} label="Accounts engaged · today" value={latest.accounts_engaged} tone="rose" />
    </div>
  );
}

function StatCard({ icon, label, value, delta, deltaLabel, tone }: {
  icon: React.ReactNode; label: string; value: number | null; delta?: number | null; deltaLabel?: string;
  tone?: 'emerald' | 'violet' | 'rose';
}) {
  const deltaColor = delta == null ? '' : delta > 0 ? 'text-emerald-500' : delta < 0 ? 'text-rose-500' : 'text-muted-foreground';
  const iconTone = tone === 'emerald' ? 'text-emerald-500' : tone === 'violet' ? 'text-violet-500' : tone === 'rose' ? 'text-rose-500' : 'text-muted-foreground';
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className={cn('flex items-center gap-2 text-xs', iconTone)}>
          {icon}
          <span className="text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-end gap-2">
          <span className="text-3xl font-semibold tabular-nums">{value?.toLocaleString('en-IN') ?? '—'}</span>
          {delta != null && (
            <span className={cn('pb-1 text-xs font-medium tabular-nums', deltaColor)}>
              {delta > 0 ? '+' : ''}{delta.toLocaleString('en-IN')}{deltaLabel && ` · ${deltaLabel}`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function BucketPerformanceTable({ rows, loading, onOpen }: { rows: BucketPerformance[]; loading: boolean; onOpen: (slug: string) => void }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (rows.length === 0) return <EmptyState title="No buckets" description="Add active buckets in the Buckets tab." />;

  const hasAnyData = rows.some((r) => r.posts > 0 || r.ads > 0);
  if (!hasAnyData) {
    return (
      <EmptyState
        title="No Studio-authored posts published yet"
        description="Once you publish a draft (or the schedule worker does), organic + paid metrics per bucket will land here."
      />
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/40 text-2xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Bucket</th>
              <th className="px-3 py-2 text-right font-medium">Posts</th>
              <th className="px-3 py-2 text-right font-medium">Reach</th>
              <th className="px-3 py-2 text-right font-medium">Interactions</th>
              <th className="px-3 py-2 text-right font-medium">Avg eng</th>
              <th className="px-3 py-2 text-right font-medium">Ads</th>
              <th className="px-3 py-2 text-right font-medium">Spend</th>
              <th className="px-3 py-2 text-right font-medium">CPM</th>
              <th className="px-3 py-2 text-right font-medium">₹/reach</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.bucket_slug} className="border-b border-border/20 last:border-0 cursor-pointer hover:bg-muted/30" onClick={() => onOpen(r.bucket_slug)}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color_accent ?? '#888' }} />
                    <span className="font-medium">{r.bucket_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.posts || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.reach_total ? compactN(r.reach_total) : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.interactions_total ? compactN(r.interactions_total) : '—'}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', r.avg_engagement_pct != null && r.avg_engagement_pct > 2 && 'text-emerald-500 font-medium')}>
                  {r.avg_engagement_pct != null ? `${r.avg_engagement_pct.toFixed(2)}%` : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.ads || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.spend_total > 0 ? `₹${Number(r.spend_total).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.avg_cpm != null ? `₹${Number(r.avg_cpm).toFixed(1)}` : '—'}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', r.cost_per_reach != null && r.cost_per_reach < 0.5 && 'text-emerald-500 font-medium')}>
                  {r.cost_per_reach != null ? `₹${Number(r.cost_per_reach).toFixed(2)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

const DOW_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function BestTimeHeatmap({ cells, loading }: { cells: BestTimeCell[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (cells.length === 0) return <EmptyState title="Not enough data yet" description="Need more posts synced to compute the heatmap." />;
  const maxEng = Math.max(...cells.map((c) => c.avg_engagement_pct ?? 0));
  const map = new Map<string, BestTimeCell>();
  cells.forEach((c) => map.set(`${c.dow}:${c.hour_ist}`, c));
  const top3 = [...cells].filter((c) => c.avg_engagement_pct != null).sort((a, b) => (b.avg_engagement_pct ?? 0) - (a.avg_engagement_pct ?? 0)).slice(0, 3);

  return (
    <Card>
      <CardContent className="space-y-3 p-3">
        {top3.length > 0 && (
          <div className="flex flex-wrap gap-2 text-2xs">
            <span className="text-muted-foreground">Best slots:</span>
            {top3.map((c, i) => (
              <span key={i} className="rounded bg-emerald-500/10 px-1.5 py-0.5 font-medium text-emerald-600 dark:text-emerald-400">
                {DOW_LABEL[c.dow]} {String(c.hour_ist).padStart(2, '0')}:00 · {c.avg_engagement_pct?.toFixed(1)}%
              </span>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-2xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="w-10"></th>
                {Array.from({ length: 24 }).map((_, h) => (
                  <th key={h} className="p-0 text-center font-normal tabular-nums">{h % 3 === 0 ? h : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 0].map((dow) => (
                <tr key={dow}>
                  <td className="pr-1 text-right font-medium text-muted-foreground">{DOW_LABEL[dow]}</td>
                  {Array.from({ length: 24 }).map((_, h) => {
                    const c = map.get(`${dow}:${h}`);
                    const intensity = c && maxEng > 0 && c.avg_engagement_pct != null ? c.avg_engagement_pct / maxEng : 0;
                    return (
                      <td key={h} className="p-0.5">
                        <div
                          className="h-4 w-full rounded-sm transition-colors"
                          style={{
                            background: c
                              ? `rgba(139, 92, 246, ${0.15 + intensity * 0.85})`
                              : 'transparent',
                          }}
                          title={c ? `${DOW_LABEL[dow]} ${h}:00 · ${c.posts} posts · ${c.avg_engagement_pct?.toFixed(1)}% eng · ${c.avg_interactions.toLocaleString('en-IN')} avg interactions` : `${DOW_LABEL[dow]} ${h}:00 · no posts`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function FollowerSparkline({ daily }: { daily: AccountDaily[] }) {
  const pts = daily.filter((d) => d.followers_count != null);
  if (pts.length < 2) return null;
  const vals = pts.map((d) => d.followers_count as number);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const W = 600, H = 60, PAD = 4;
  const path = pts.map((d, i) => {
    const x = PAD + (i / (pts.length - 1)) * (W - PAD * 2);
    const y = H - PAD - ((d.followers_count! - min) / range) * (H - PAD * 2);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <Card>
      <CardContent className="p-3">
        <div className="mb-1 flex items-baseline justify-between text-xs">
          <span className="tabular-nums text-muted-foreground">{pts[0]?.captured_on ? new Date(pts[0].captured_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}</span>
          <span className="text-2xs uppercase text-muted-foreground">followers</span>
          <span className="tabular-nums text-muted-foreground">{pts[pts.length - 1]?.captured_on ? new Date(pts[pts.length - 1]!.captured_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}</span>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="h-16 w-full">
          <path d={path} fill="none" stroke="rgb(139, 92, 246)" strokeWidth="1.5" />
        </svg>
        <div className="flex items-baseline justify-between text-2xs tabular-nums text-muted-foreground">
          <span>{min.toLocaleString('en-IN')}</span>
          <span>{max.toLocaleString('en-IN')}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function TopPostsGrid({ posts, loading }: { posts: TopPostRow[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (posts.length === 0) return <EmptyState title="No posts in window" description="Post something and it'll rank here after a day." />;
  return (
    <div className="grid gap-2 sm:grid-cols-3 md:grid-cols-6">
      {posts.map((p) => (
        <a key={p.media_id} href={p.permalink ?? '#'} target="_blank" rel="noreferrer" className="group block">
          <div className="relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted/30">
            {p.thumbnail_url && (
              <img src={p.thumbnail_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-2xs text-white tabular-nums">
              <span className="flex items-center gap-0.5"><Heart className="h-2.5 w-2.5" /> {p.like_count}</span>
              <span className="flex items-center gap-0.5">💬 {p.comments_count}</span>
              {p.reach != null && <span className="ml-auto opacity-80">reach {compactN(p.reach)}</span>}
            </div>
          </div>
          {p.caption && (
            <div className="mt-1 line-clamp-1 text-2xs text-muted-foreground">{p.caption}</div>
          )}
        </a>
      ))}
    </div>
  );
}
function compactN(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

function ReelsLeaderboard({ reels, loading }: { reels: ReelRow[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  if (reels.length === 0) {
    return <EmptyState title="No reels in the last 30 days" description="Post a reel or refresh account insights." />;
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/40 text-2xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Reel</th>
              <th className="px-3 py-2 text-right font-medium">Plays</th>
              <th className="px-3 py-2 text-right font-medium">Reach</th>
              <th className="px-3 py-2 text-right font-medium">Saves</th>
              <th className="px-3 py-2 text-right font-medium">Avg watch</th>
              <th className="px-3 py-2 text-right font-medium">♥</th>
            </tr>
          </thead>
          <tbody>
            {reels.map((r) => (
              <tr key={r.media_id} className="border-b border-border/20 last:border-0 hover:bg-muted/30">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {r.thumbnail_url && (
                      <img src={r.thumbnail_url} alt="" referrerPolicy="no-referrer" className="h-8 w-8 shrink-0 rounded object-cover" />
                    )}
                    <a
                      href={r.permalink ?? '#'}
                      target="_blank" rel="noreferrer"
                      className="line-clamp-1 min-w-0 flex-1 truncate text-xs text-foreground hover:text-violet-500"
                      title={r.caption ?? ''}
                    >
                      {r.caption?.slice(0, 60) ?? r.media_id}
                    </a>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.views?.toLocaleString('en-IN') ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.reach?.toLocaleString('en-IN') ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.saved?.toLocaleString('en-IN') ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.avg_watch_time_s != null ? `${r.avg_watch_time_s}s` : '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.like_count.toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function CoverageCard({ row, onOpen }: { row: BucketCoverage; onOpen: () => void }) {
  const pct = row.target ? Math.min(100, Math.round((row.count_month / row.target) * 100)) : null;
  const status = pct == null ? 'no target' : pct >= 80 ? 'on track' : pct >= 40 ? 'behind' : 'gap';
  const tone = status === 'on track' ? 'emerald' : status === 'behind' ? 'amber' : status === 'gap' ? 'rose' : 'muted';
  return (
    <Card className="cursor-pointer transition-colors hover:border-violet-500/50" onClick={onOpen}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full" style={{ background: row.color }} />
          <div className="text-sm font-medium">{row.name}</div>
          <span className={cn('ml-auto rounded-full px-2 py-0.5 text-2xs',
            tone === 'emerald' && 'bg-emerald-500/10 text-emerald-500',
            tone === 'amber' && 'bg-amber-500/10 text-amber-500',
            tone === 'rose' && 'bg-rose-500/10 text-rose-500',
            tone === 'muted' && 'bg-muted/50 text-muted-foreground',
          )}>{status}</span>
        </div>
        <div className="flex items-end gap-2">
          <span className="text-3xl font-semibold tabular-nums">{row.count_month}</span>
          {row.target && <span className="pb-1 text-sm text-muted-foreground tabular-nums">/ {row.target}</span>}
        </div>
        {pct != null && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: row.color }} />
          </div>
        )}
        <div className="text-2xs text-muted-foreground">{row.count_this_week} this week</div>
      </CardContent>
    </Card>
  );
}

function AnalysisResult({ r, coverage }: { r: AnalyzeResp; coverage: BucketCoverage[] }) {
  const perBucket = r.per_bucket ?? {};
  const suggested = r.suggested_buckets ?? [];
  const postsAnalyzed = r.posts_analyzed ?? r.posts ?? 0;

  if (postsAnalyzed === 0) {
    return (
      <EmptyState
        title="No IG posts in the last 90 days"
        description={r.note ?? 'meta-sync hasn\'t landed fresh @flaxitup posts yet — the analysis needs recent posts to classify.'}
      />
    );
  }

  const colors = new Map(coverage.map((c) => [c.slug, c.color]));
  const rows = Object.entries(perBucket).sort((a, b) => b[1].count - a[1].count);
  const total = rows.reduce((s, [, b]) => s + b.count, 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Lightbulb className="h-4 w-4 text-amber-500" /> Recommendation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm leading-relaxed">{r.recommendation ?? '—'}</p>
          <div className="mt-2 text-2xs text-muted-foreground">
            Based on {postsAnalyzed} posts over the last {r.days} days.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Actual feed distribution</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
            {total > 0 && rows.map(([slug, b]) => (
              <div key={slug} className="h-full transition-all" style={{ width: `${(b.count / total) * 100}%`, background: colors.get(slug) ?? '#888' }} title={`${slug} · ${b.count}`} />
            ))}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {rows.map(([slug, b]) => (
              <div key={slug} className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/10 p-2 text-2xs">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colors.get(slug) ?? '#888' }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium capitalize">{slug}</span>
                    <span className="ml-auto tabular-nums text-muted-foreground">
                      {b.count} · {b.count > 0 ? Math.round(b.likes / b.count) : 0}♥ avg
                    </span>
                  </div>
                  {b.sample_captions[0] && (
                    <div className="mt-0.5 line-clamp-1 text-muted-foreground/80">{b.sample_captions[0]}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {suggested.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Suggested new buckets</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {suggested.map((b) => (
              <div key={b.slug} className="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
                <div className="text-sm font-medium">{b.name} <span className="ml-1 rounded bg-white/20 px-1.5 py-0.5 text-2xs font-normal text-muted-foreground">{b.slug}</span></div>
                <div className="mt-0.5 text-xs text-muted-foreground">{b.description}</div>
                <div className="mt-1 text-2xs italic text-muted-foreground/80">"{b.rationale}"</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
